import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import localization from "../../data/config";
import { useSelector } from "react-redux";
import { setPage as setPageAction }  from "../../actions/parts";
import { usePrevious } from "../../utilities/hooks";
import {
  PAGE_FIRST,
  PAGE_LAST,
  PAGE_NEXT,
  PAGE_PREVIOUS,
  ALL_PAGE_SIZES
} from "../../constants/config/parts";
import TableCount from "./TableCount";
import "./style.css";
import previousArrowEnabledIcon from "../../img/icons/previous-arrow-enabled.svg";
import previousArrowDisabledIcon from "../../img/icons/previous-arrow-disabled.svg";
import nextArrowEnabledIcon from "../../img/icons/next-arrow-enabled.svg";
import nextArrowDisabledIcon from "../../img/icons/next-arrow-disabled.svg";
import dropDownArrowIcon from "../../img/icons/dropdown-arrow-pagination.svg";
import "./bootstrap-override.css";

/**
 * @name Pagination
 * @function
 * @description Displays action controls for the table
 * @returns {JSX}
 * @param {*} props
 */
function Pagination(props) {
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [disabledPrevious, setDisabledPrevious] = useState(true),
  dispatch = useDispatch(),
  dropdownContainerRef = useRef(),
  { count, currentPage, total, labels, pageSize } = useSelector((state) => ({
    count: state.parts.count,
    currentPage: state.parts.currentPage,
    total: state.parts.pageCount,
    labels: {
      pageReq: state.labels.pageReq,
      totalPages: state.labels.totalPages,
      itemsLabel: state.labels.items,
      pageLabel: state.labels.page,
      ofLabel: state.labels.of
    },
    pageSize: state.parts.pageSize
  })),
    [isOpen, setIsOpen] = useState(false),
    [disabledNext, setDisabledNext] = useState(false),
    [page, setPage] = useState(currentPage),
    [pageInput, setPageInput] = useState(page),
    prevPage = usePrevious(page),
    prevPageSize = usePrevious(pageSize),
    [currentPageStartIndex, setCurrentPageStartIndex] = useState(1),
    [currentPageLastIndex, setCurrentPageLastIndex] = useState(pageSize),
    options = Array.from({ length: total }, (_, index) => index + 1);
 
    //useEffect to handle the closing of the dropdown on outside click
    useEffect(() => {
      function handleClickOutside(event) {
        if (dropdownContainerRef.current && !dropdownContainerRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

  useEffect(() => {
    if ((prevPage !== page && page !== currentPage) || prevPageSize !== pageSize) {
      // get actual results
      dispatch(setPageAction(page));
      // disable or enable next and previous
      setDisabledNext(page === total);
      setDisabledPrevious(page <= 1 || page==="");
    }
  });

  //sets the info on the item index range on bottom left
  useEffect(() => {
    if (page === 1) {
      setCurrentPageStartIndex(1);
      setCurrentPageLastIndex(pageSize > count ? count : pageSize);
      setDisabledPrevious(true);
      setDisabledNext(pageSize > count ? true : false );
    } else {
      if (pageSize * page < count) setCurrentPageLastIndex(pageSize * page);
      else {
        setCurrentPageLastIndex(count);
        setDisabledPrevious(false);
        setDisabledNext(true);
      }
      setCurrentPageStartIndex(pageSize * (page - 1) + 1);
    }
  }, [page, pageSize]);

  /**
   * use effect to set the page number when page size is changed. 
   */
  useEffect(() => {
    setPage(1);
    setPageInput(1);
    scrollToTopOfTable();
  }, [pageSize]);

  /**
 * use effect to redirect the page to page 1 when filters are changes
 * @effect
 * @param {number} count - The dependency triggering the effect when changed.
 * @returns {void}
 */
  useEffect(()=>{
    setPage(1);
    setPageInput(1);
  }, [count]);

  // Sync with currentPage
  useEffect( ()=>{
    setPage( currentPage );
    setPageInput( currentPage );
  }, [ currentPage ]);

  /**
   * @name scrollToTopOfTable
   * @function
   * @description scrolls to the top of the table.
   */
  const scrollToTopOfTable = () => {
    const desktopTable = document.querySelector(".pst_table");
    const mobileProductCard = document.querySelector(".product__cards__container")
    let top = 0;
    if (desktopTable) {
      const manageContainer = document.querySelector(".managecontainer");
      top = desktopTable.offsetTop - (manageContainer ? manageContainer.offsetHeight : 0);
    } else if (mobileProductCard) {
      const filterContainer = document.querySelector(".filterlayout_container");
      top = mobileProductCard.offsetTop - (filterContainer ? filterContainer.offsetHeight : 0);
    }

    window.scrollTo({
      top,
      behavior: "smooth",
    })
  }

  /**
   * @name handleButton
   * @function
   * @description Update current page based on the button click type
   */
  const handleButton = (e) => {
    e.preventDefault();
    e.stopPropagation();
    switch (e.currentTarget.dataset.value) {
      case PAGE_FIRST:
        setPage(1);
        setPageInput(1);
        break;
      case PAGE_LAST:
        setPage(total);
        setPageInput(total);
        break;
      case PAGE_NEXT:
        setPage(page + 1);
        setPageInput(page + 1);
        break;
      case PAGE_PREVIOUS:
        if (page > 1) {
          setPage(page - 1);
          setPageInput(page - 1);
        }
        break;
      default:
        setPage(1);
        setPageInput(1);
        break;     
      }

      scrollToTopOfTable();
    }

  

  //In case, we need this in the future
  // let delayTimer;
  // const debounce = (func, delay) => {
  //   clearTimeout(delayTimer);
  //   delayTimer = setTimeout(func, delay);
  // };

  /**
   * @name handleText
   * @function
   * @description
   * @param {EventListenerObject} e Synthetic browser event
   */
  const handleText = (e) => {
    setIsOpen(false);
    let newPage = Number(e.target.value);
    if (isNaN(newPage)) return;
    if (newPage === 0) {
      setPageInput("");
    }
    if (newPage > 0 && newPage <= total) {
      setPageInput(newPage);
      setPage(newPage);
    }
  };

  /**
   * @name handleDropDownClick
   * @function
   * @description handles click event of dropdown inside input tag
   */
  const handleDropDownClick = (option) => {
    setPage(option);
    setPageInput(option);
    setIsOpen(false);

    if (option <= 1) {
      setDisabledPrevious(option <= 1);
      if (option !== total) {
        setDisabledNext(false);
      }
    }
    if (option == total) {
      setDisabledNext(option);
    }
    scrollToTopOfTable();
  };

  /**
   * @name handleToggle
   * @function
   * @description handles the open and closed state of dropdown 
   */
  const handleToggle = () => {
    setIsOpen(!isOpen);
  };
  
  if (total <= 1 && count <= ALL_PAGE_SIZES[0]) return null;

  /**
   * @name handleKeyDown
   * @function
   * @description Handles the keys arrows Up and Down, Focus, Enter and Escape for accessibility
   */
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        const direction = e.key === 'ArrowDown' ? 1 : -1;
        const options = document.querySelectorAll('.dropdown-options-pagination__option');
        let newIndex = focusedIndex + direction;
  
        if (newIndex < 0) {
          newIndex = options.length - 1;
        } else if (newIndex >= options.length) {
          newIndex = 0;
        }
  
        setFocusedIndex(newIndex);
        options[newIndex].focus();
        break;
  
      case 'Enter':
        if (e.key ==="Enter") {
          setIsOpen(!isOpen)
        } 
        break;

        case 'Escape':
          if (e.key ==="Escape") {
            setIsOpen(false)
          } 
        break;
      
  
      default:
        break;
    }
  };


  return (
    <nav className="pagination__nav">
      <div className="pagination_nav-left pagination_nav-flex pagination_nav-rpp">
        <p>
          {currentPageStartIndex} - {currentPageLastIndex} {labels.ofLabel} {count} {labels.itemsLabel}
        </p>
        <TableCount />
      </div>
      <div className="navigation-controls">
        <button
          type="button"
          role="button"
          aria-label = {localization.prevButton}
          onClick={handleButton}
          data-value={PAGE_PREVIOUS}
          disabled={disabledPrevious}
          className="arrow-button"
        >
          <img
            src={
              disabledPrevious
                ? previousArrowDisabledIcon
                : previousArrowEnabledIcon
            }
            className="arrow-icon previous-arrow-icon"
          />
        </button>
        {props.extraText && <span className="page-text__text">{labels.pageLabel}</span>}
        <div className="input-container" ref={dropdownContainerRef} onKeyDown={handleKeyDown}>
          <input
            type="text"
            value={pageInput}
            onChange={handleText}
            className="page-input"
            aria-label={localization.pageInput}
            tabIndex={0}
            aria-expanded={`${isOpen ? "true" : "false"}`}
            role="combobox"
            aria-autocomplete="list"
          />
          <img
            className={`dropdown-icon ${isOpen ? "open" : ""}`}
            src={dropDownArrowIcon}
            onClick={handleToggle}
            alt="dropdown-icon"
            width="24px"
            height="24px"
          />
          {isOpen && (
            <ul className="dropdown-options-pagination">
              {options.map((option, index) => (
                <li
                  className="dropdown-options-pagination__option"
                  tabIndex={0}
                  onClick={() => handleDropDownClick(option)}
                >
                  {option}
                </li>
              ))}
            </ul>
          )}
        </div>

        {props.extraText && 
          <span className="page-text__total">
            {labels.totalPages.replace("{total}", total)}
          </span>
        }
        <button
          type="button"
          role="button"
          aria-label = {localization.nextButton}
          onClick={handleButton}
          data-value={PAGE_NEXT}
          disabled={disabledNext}
          className="arrow-button"
        >
          <img
            src={disabledNext ? nextArrowDisabledIcon : nextArrowEnabledIcon}
            className="arrow-icon next-arrow-icon"
          />
        </button>
      </div>
    </nav>
  );
}

Pagination.defaultProps = {
  currentPage: 1,
  total: 1,
  extraText: true,
};

Pagination.propTypes = {
  extraText : PropTypes.bool
};

export default Pagination;
